import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { GlassCard, StatusDot } from "../gc/ui";
import { computeProgress, statusBreakdown, allTags, STATUS_MAP, STORY_TYPES, replaceDemoKit } from "../../lib/gamecraft";
import StudioBoard from "./StudioBoard";
import {
  Lightbulb, Palette, Code, MusicNotes, Cpu, Plus, Play,
} from "@phosphor-icons/react";

import { useAuth } from "../../contexts/AuthContext";
import { greetingLine, roleLabel } from "../../lib/profile";
import { useI18n } from "../../lib/i18n";

export default function OverviewPanel({ project, onJump, onChange }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const sprites = project.sprites || [];
  const files = project.files || [];
  const samples = project.samples || [];
  const stories = project.story_items || [];
  const progress = computeProgress(project);
  const breakdown = statusBreakdown(project);
  const tags = allTags(project);
  const modules = (project.idea_modules || []).filter((m) => m.enabled !== false);
  const pie = Object.entries(breakdown).map(([k, v]) => ({ name: STATUS_MAP[k]?.label || k, value: v, color: STATUS_MAP[k]?.color }));
  const pieTotal = pie.reduce((s, x) => s + x.value, 0) || 1;
  const activity = (project.activity || []).slice(-6).reverse();
  const kanban = project.kanban?.columns || [];

  const quick = [
    { id: "ideas", label: "Nueva idea", icon: Lightbulb, tab: "ideas" },
    { id: "sprites", label: "Nuevo sprite", icon: Palette, tab: "sprites" },
    { id: "audio", label: "Nuevo sonido", icon: MusicNotes, tab: "audio" },
    { id: "code", label: "Nuevo script", icon: Code, tab: "code" },
    { id: "canvas", label: "Nueva tarea", icon: Plus, tab: "canvas" },
    { id: "engine", label: "Probar en motor", icon: Play, tab: "engine" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
      <GlassCard className="p-0 overflow-hidden">
        <div className="px-6 py-5 text-white gc-banner" style={{ background: "var(--gc-banner)" }}>
          <div className="text-sm opacity-80">{greetingLine(user, lang)}</div>
          {(user?.studio || user?.role) && (
            <div className="text-xs opacity-70 mt-0.5">
              {[user.studio, roleLabel(user.role)].filter(Boolean).join(" · ")}
            </div>
          )}
          <h1 className="font-display text-2xl md:text-3xl font-bold mt-1">{project.name}</h1>
          <p className="text-sm opacity-80 mt-1 line-clamp-2">{project.gdd?.idea || project.description || "Define la idea general en Proyecto."}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            {[
              { l: "Módulos", v: modules.length },
              { l: "Tareas", v: kanban.reduce((s, c) => s + (c.cards || []).length, 0) },
              { l: "Assets", v: sprites.length + samples.length },
              { l: "Progreso", v: `${progress}%` },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white/15 px-4 py-3 backdrop-blur">
                <div className="text-[11px] opacity-80">{s.l}</div>
                <div className="text-2xl font-bold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard className="p-4">
          <h3 className="font-semibold mb-3">Actividad reciente</h3>
          <div className="space-y-2">
            {activity.length === 0 && <p className="text-sm" style={{ color: "var(--gc-muted)" }}>Aún no hay actividad.</p>}
            {activity.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <StatusDot status="approved" size={8} className="mt-1.5" />
                <span>{a.message}</span>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-4 lg:col-span-2">
          <h3 className="font-semibold mb-3">Acciones rápidas</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {quick.map((q) => (
              <button key={q.id} onClick={() => onJump(q.tab)} className="gc-btn gc-btn-ghost h-11 px-3 justify-start">
                <q.icon size={16} className="text-sky-500" /> {q.label}
              </button>
            ))}
            <button
              className="gc-btn gc-btn-ghost h-11 px-3 justify-start col-span-2 md:col-span-3"
              onClick={() => {
                if (!window.confirm("¿Actualizar el kit de demo? Conserva sprites e ideas que no sean del kit inicial.")) return;
                onChange(replaceDemoKit(project));
              }}
            >
              Actualizar / reemplazar kit de demo
            </button>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard className="p-4" hover onClick={() => onJump("ideas")}>
          <h3 className="font-semibold mb-3">Ideas del proyecto</h3>
          <div className="space-y-2">
            {modules.map((m) => {
              const list = project.entities?.[m.id] || [];
              const statuses = ["approved", "thinking", "cut", "neutral"].map((s) => list.some((e) => (e.status || "neutral") === s) ? s : null).filter(Boolean);
              return (
                <div key={m.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                    {m.name}
                    <div className="flex gap-0.5">{statuses.map((s) => <StatusDot key={s} status={s} size={6} />)}</div>
                  </div>
                  <span className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>{list.length}</span>
                </div>
              );
            })}
          </div>
        </GlassCard>

        <GlassCard className="p-4" hover onClick={() => onJump("story")}>
          <h3 className="font-semibold mb-3">Historia & Mundo</h3>
          <div className="space-y-2">
            {STORY_TYPES.map((t) => {
              const list = stories.filter((s) => s.type === t.id);
              const top = list[0];
              return (
                <div key={t.id} className="flex items-center justify-between text-sm">
                  <span>{t.name}</span>
                  <span className="flex items-center gap-2 text-xs">
                    {top && <span className="gc-chip" style={{ background: `${STATUS_MAP[top.status || "neutral"]?.color}22`, color: STATUS_MAP[top.status || "neutral"]?.color }}>{STATUS_MAP[top.status || "neutral"]?.label}</span>}
                    {list.length}
                  </span>
                </div>
              );
            })}
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <h3 className="font-semibold mb-3">Etiquetas & estado</h3>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tags.slice(0, 16).map((t) => <span key={t} className="gc-chip">#{t}</span>)}
            {tags.length === 0 && <span className="text-xs" style={{ color: "var(--gc-muted)" }}>Añade etiquetas en Ideas.</span>}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-28 h-28">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" innerRadius={28} outerRadius={44} paddingAngle={2}>
                    {pie.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs space-y-1">
              {pie.map((d) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  {d.name} {Math.round((d.value / pieTotal) * 100)}%
                </div>
              ))}
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard className="p-4" hover onClick={() => onJump("engine")}>
          <h3 className="font-semibold mb-3">Vista rápida en motor</h3>
          <div className="rounded-2xl h-40 flex items-center justify-center overflow-hidden" style={{ background: "#0f172a" }}>
            {sprites[0] ? (
              <img src={sprites[0].data_url} alt="" className="max-h-full" style={{ imageRendering: "pixelated" }} />
            ) : (
              <span className="text-xs text-slate-400">Sin sprites aún</span>
            )}
          </div>
          <button className="gc-btn gc-btn-primary w-full h-10 mt-3" onClick={(e) => { e.stopPropagation(); onJump("engine"); }}>
            <Cpu size={14} /> Abrir Motor de Pruebas
          </button>
        </GlassCard>

        <GlassCard className="p-4" hover onClick={() => onJump("sprites")}>
          <h3 className="font-semibold mb-3">Sprites recientes</h3>
          <div className="grid grid-cols-4 gap-2">
            {sprites.slice(-4).reverse().map((s) => (
              <div key={s.id} className="aspect-square rounded-xl overflow-hidden flex items-center justify-center" style={{ background: "var(--gc-bg-accent)" }}>
                <img src={s.data_url} alt={s.name} className="max-w-full max-h-full" style={{ imageRendering: "pixelated" }} />
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-4" hover onClick={() => onJump("code")}>
          <h3 className="font-semibold mb-3">Editor de código</h3>
          <pre className="gc-tool p-3 text-[11px] leading-relaxed h-40 overflow-hidden font-mono">
            {(files[0]?.content || "// Crea tu primer script").slice(0, 320)}
          </pre>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { t: "SFX", n: samples.filter((s) => (s.category || "").toUpperCase() === "SFX").length, tab: "audio", d: "Efectos listos" },
          { t: "Música", n: samples.filter((s) => (s.category || "").toUpperCase() === "BGM").length, tab: "audio", d: "Pistas / loops" },
          { t: "Ambiente", n: samples.filter((s) => (s.category || "").toUpperCase() === "AMBIENCE").length, tab: "audio", d: "Capas de mundo" },
          { t: "Playtest", n: sprites.length, tab: "engine", d: "Sprites en motor" },
        ].map((x) => (
          <GlassCard key={x.t} hover className="p-4" onClick={() => onJump(x.tab)}>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--gc-muted)" }}>{x.d}</div>
            <div className="text-2xl font-bold mt-1">{x.n}</div>
            <div className="text-sm font-semibold">{x.t}</div>
            <span className="gc-eq mt-3"><i /><i /><i /><i /></span>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-4" hover onClick={() => onJump("canvas")}>
        <h3 className="font-semibold mb-3">Canvas & Kanban</h3>
        <div className="grid grid-cols-3 gap-3">
          {kanban.map((c) => (
            <div key={c.id} className="rounded-2xl p-3" style={{ background: "var(--gc-bg-accent)" }}>
              <div className="text-[11px] font-bold uppercase mb-2" style={{ color: "var(--gc-muted)" }}>{c.title}</div>
              {(c.cards || []).slice(0, 3).map((card) => (
                <div key={card.id} className="text-xs py-1 truncate">{card.title}</div>
              ))}
            </div>
          ))}
        </div>
      </GlassCard>

      <StudioBoard project={project} onChange={onChange} onJump={onJump} />
    </div>
  );
}
